import {useEffect, useId, useRef, useState} from "react";
import {useBaseLayer, useDownloadMapAsPng, useMap} from "base/component/geo";
import {useMapProvider} from "sisbagal/map";
import SisbagalMapActions from "./SisbagalMapActions";
import Stack from "@mui/material/Stack";
import {NO_CONTENT_HEIGHT} from "base/config/measurements";
import {useSisbagalAppConfig} from "sisbagal/provider";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SisbagalSectorSelector from "sisbagal/menu/SisbagalSectorSelector";
import SisbagalQuickActions from "sisbagal/menu/SisbagalQuickActions";

const mapStyle = {
    width: "100%",
    height: `calc(100vh - ${NO_CONTENT_HEIGHT}px)`,
};

const SisbagalMap = ({
    legend,
    mapOptions = {},
    showDownloadPdfAction = false,
    entityData = {},
    height = null,
    handleClickOnMap = null,
}) => {
    const [panelOpen, setPanelOpen] = useState(true);
    const [layersOpen, setLayersOpen] = useState(true);
    const panelId = useId();
    const layersId = useId();
    const {getAllLayerProviders, getIncludedLayerProviders, setMap} = useMapProvider();
    const {selectedBaseLayer} = useSisbagalAppConfig();
    const {setBaseLayer} = useBaseLayer();

    const {mapRef, createMap} = useMap();
    const mapObjectRef = useRef(null);

    const {
        setConfig: setConfigMapAsPng,
        download: handleDownloadMapAsPng,
        generate: generateMapAsPng,
        getDimensions: getMapAsPngDimensions,
    } = useDownloadMapAsPng();

    setConfigMapAsPng(mapObjectRef.current, mapRef.current);

    useEffect(() => {
        const map = createMap(mapOptions);
        if (setMap) {
            setMap(map);
        }
        getIncludedLayerProviders().forEach(layerProvider =>
            layerProvider.layer ? layerProvider.layer.createLayer(map) : null
        );
        mapObjectRef.current = map;
        // Leaflet must recompute its viewport as the sidebar changes width.
        const resizeObserver = new ResizeObserver(() => {
            map.invalidateSize({animate: false, debounceMoveend: true});
        });
        resizeObserver.observe(mapRef.current);

        return () => {
            resizeObserver.disconnect();
            if (map._loaded) {
                map.remove();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (mapObjectRef.current) {
            setBaseLayer(mapObjectRef.current, selectedBaseLayer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBaseLayer]);

    getAllLayerProviders().forEach(layerProvider => layerProvider.useProvider());

    return (
        <Stack
            direction="row"
            sx={{width: "100%", height: height || mapStyle.height, minWidth: 0}}
        >
            <div
                id="map"
                style={{...mapStyle, height: "100%", flex: 1, minWidth: 0}}
                ref={mapRef}
            />
            <Box
                id={panelId}
                aria-hidden={!panelOpen}
                inert={panelOpen ? undefined : ""}
                sx={{
                    width: panelOpen ? 320 : 0,
                    flexShrink: 0,
                    overflow: "hidden",
                    transition: "width 250ms ease",
                    bgcolor: "secondary.lighter",
                }}
            >
                <Stack
                    sx={{
                        width: 320,
                        height: "100%",
                        overflowY: "auto",
                        borderLeft: "1px solid",
                        borderColor: "secondary.main",
                        boxSizing: "border-box",
                        transform: panelOpen ? "translateX(0)" : "translateX(-100%)",
                        transition: "transform 250ms ease",
                        "& > *": {flexShrink: 0},
                        "& .MapMenuBox": {
                            height: "auto",
                            overflow: "visible",
                            minWidth: 0,
                        },
                    }}
                >
                    <SisbagalQuickActions
                        canGenerateReport={showDownloadPdfAction}
                        generateMapAsPng={generateMapAsPng}
                        getMapAsPngDimensions={getMapAsPngDimensions}
                        entityData={entityData}
                    />
                    <SisbagalSectorSelector />
                    <SisbagalMapActions
                        expanded={layersOpen}
                        onToggle={() => setLayersOpen(value => !value)}
                        contentId={layersId}
                        generateMapAsPng={generateMapAsPng}
                        getMapAsPngDimensions={getMapAsPngDimensions}
                        handleDownloadMapAsPng={handleDownloadMapAsPng}
                        entityData={entityData}
                        showDownloadPdfAction={showDownloadPdfAction}
                    />
                    <Collapse in={layersOpen} id={layersId}>
                        {legend}
                    </Collapse>
                </Stack>
            </Box>
            <Box
                sx={{
                    width: 32,
                    flexShrink: 0,
                    bgcolor: "secondary.light",
                    borderLeft: "1px solid",
                    borderColor: "secondary.main",
                }}
            >
                <Tooltip
                    title={
                        panelOpen ? "Ocultar panel do mapa" : "Mostrar panel do mapa"
                    }
                >
                    <IconButton
                        size="small"
                        onClick={() => setPanelOpen(value => !value)}
                        aria-label={
                            panelOpen
                                ? "Ocultar panel do mapa"
                                : "Mostrar panel do mapa"
                        }
                        aria-expanded={panelOpen}
                        aria-controls={panelId}
                    >
                        {panelOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </Tooltip>
            </Box>
        </Stack>
    );
};

export default SisbagalMap;
